import React, { useState, useEffect, useRef } from 'react';
import { 
  Input, 
  Button, 
  Select, 
  Typography, 
  Tag, 
  Divider, 
  Spin,
  message,
  Tooltip
} from 'antd';
import { 
  SendOutlined, 
  RobotOutlined, 
  UserOutlined, 
  BookOutlined,
  PlusOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import './InteractiveChat.css';
import { getConfig, getConfigAsync } from '../../utils/config';
import { useAppTheme } from '../../theme/ThemeProvider';
import { useUsageTracking } from '../../hooks/useUsageTracking';
import { logger } from '../../utils/logger';

const { TextArea } = Input;
const { Text, Title } = Typography;
const { Option } = Select;

interface SavedSignal {
  id: string;
  name: string;
  symbol: string;
  timeframe: string;
  time_range: {
    start_time: string; // ISO datetime format (e.g., "2025-07-21T14:00:00")
    end_time: string;   // ISO datetime format (e.g., "2025-07-22T01:00:00")
  };
  analysis_data: any;
  created_at: string; // ISO datetime format
  updated_at: string; // ISO datetime format
  description?: string;
}

interface ChatMessage {
  role: 'user' | 'ai';
  content: string;
  timestamp: Date;
  signalId?: string;
}

interface InteractiveChatProps {
  onSignalSelect?: (signal: SavedSignal | null) => void;
  height?: number | string;
}

const InteractiveChat: React.FC<InteractiveChatProps> = ({ onSignalSelect, height = '100%' }) => {
  const { t, i18n } = useTranslation();
  const { theme } = useAppTheme();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [signals, setSignals] = useState<SavedSignal[]>([]);
  const [selectedSignals, setSelectedSignals] = useState<string[]>([]);
  const [streamingMessage, setStreamingMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  
  // 使用次数跟踪
  const { canUse, checkAndRecordUsage, showUsageExceededMessage } = useUsageTracking();

  const getApiBaseUrl = async () => {
    const config = await getConfigAsync();
    return config?.API_BASE_URL || 'https://getaws.alpha-pilot.net';
  };

  // 检查是否滚动到底部
  const isScrolledToBottom = () => {
    if (!chatContainerRef.current) return true;
    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    return scrollHeight - scrollTop - clientHeight < 10; // 10px 容差
  };

  // 处理滚动事件
  const handleScroll = () => {
    if (chatContainerRef.current) {
      const isAtBottom = isScrolledToBottom();
      setShouldAutoScroll(isAtBottom);
    }
  };

  // 加载信号列表
  useEffect(() => {
    loadSignals();
  }, []);

  // 自动滚动到底部 - 只有在应该自动滚动时才滚动
  useEffect(() => {
    if (shouldAutoScroll) {
      // Only scroll the chat container, never the whole page.
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTo({ top: chatContainerRef.current.scrollHeight, behavior: 'smooth' });
        return;
      }
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages, streamingMessage, shouldAutoScroll]);

  const loadSignals = async () => {
    try {
      const apiBaseUrl = await getApiBaseUrl();
      const { getApiHeadersWithoutContentType } = await import('../../utils/apiHeaders');
      const response = await fetch(`${apiBaseUrl}/signal/list`, {
        headers: getApiHeadersWithoutContentType()
      });
      const data = await response.json();
      if (data.success) {
        setSignals(data.signals);
      }
    } catch (error) {
      logger.error('加载信号失败:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!input.trim()) return;

    // 检查使用次数限制
    const canProceed = await checkAndRecordUsage();
    if (!canProceed) {
      return;
    }

    const userMessage: ChatMessage = {
      role: 'user',
      content: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    setStreamingMessage('');
    setShouldAutoScroll(true); // 重置为自动滚动

    try {
      // 获取选中的信号数据
      const selectedSignalData = signals.filter(s => selectedSignals.includes(s.id));
      
      const apiBaseUrl = await getApiBaseUrl();
      const { getApiHeaders } = await import('../../utils/apiHeaders');
      const response = await fetch(`${apiBaseUrl}/chat/signal`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify({
          message: input,
          signals: selectedSignalData,
          selectedSignalIds: selectedSignals,
          language: i18n.language
        })
      });

      if (!response.ok) {
        throw new Error('网络请求失败');
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('无法读取响应流');
      }

      let aiMessage = '';
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              const content = data.choices?.[0]?.delta?.content;
              if (content) {
                aiMessage += content;
                setStreamingMessage(aiMessage);
              }
            } catch (e) {
              // 忽略解析错误
            }
          }
        }
      }

      // IMPORTANT: Clear streaming message FIRST to prevent duplicate rendering
      // (streaming bubble disappears before final message appears)
      setStreamingMessage('');
      
      const finalMessage: ChatMessage = {
        role: 'ai',
        content: aiMessage,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, finalMessage]);

    } catch (error) {
      logger.error('发送消息失败:', error);
      message.error('发送消息失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSignalSelect = (signalIds: string[]) => {
    setSelectedSignals(signalIds);
    
    // 调用父组件的回调，传递第一个选中的信号
    if (onSignalSelect) {
      const firstSelectedSignal = signalIds.length > 0 
        ? signals.find(s => s.id === signalIds[0]) 
        : null;
      onSignalSelect(firstSelectedSignal || null);
    }
  };

  const handleRemoveSignal = (signalId: string) => {
    const newSignals = selectedSignals.filter(id => id !== signalId);
    setSelectedSignals(newSignals);
    
    // 调用父组件的回调，传递第一个剩余的信号或null
    if (onSignalSelect) {
      const firstRemainingSignal = newSignals.length > 0 
        ? signals.find(s => s.id === newSignals[0]) 
        : null;
      onSignalSelect(firstRemainingSignal || null);
    }
  };

  const getSelectedSignalNames = () => {
    return signals
      .filter(s => selectedSignals.includes(s.id))
      .map(s => s.name);
  };

  const formatTimestamp = (timestamp: string) => {
    // 处理ISO datetime字符串格式
    const date = new Date(timestamp);
    return date.toLocaleString('zh-CN', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div 
      style={{ 
        height: height,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
          padding: '16px',
          background: theme.surfaceBackground,
          color: theme.textPrimary,
        boxSizing: 'border-box'
      }}
    >
      {/* 信号选择区域 */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 8 }}>
          <Text strong>{t('chat.selectSignal')}：</Text>
          <Tooltip title={t('chat.selectSignal')}>
            <Text type="secondary" style={{ fontSize: '12px', marginLeft: 8, color: theme.textSecondary }}>
              {t('chat.multipleSelect')}
            </Text>
          </Tooltip>
        </div>
        
        <Select
          mode="multiple"
          placeholder={t('chat.selectSignal')}
          value={selectedSignals}
          onChange={handleSignalSelect}
          style={{ width: '100%' }}
          optionFilterProp="children"
        >
          {signals.map(signal => (
            <Option key={signal.id} value={signal.id}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>{signal.name}</span>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {formatTimestamp(signal.created_at)}
                </Text>
              </div>
            </Option>
          ))}
        </Select>

        {/* 已选择的信号标签 */}
        {selectedSignals.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <Text type="secondary" style={{ fontSize: '12px', color: theme.textSecondary }}>已选择：</Text>
            <div style={{ marginTop: 4 }}>
              {getSelectedSignalNames().map((name, index) => (
                <Tag
                  key={index}
                  closable
                  onClose={() => handleRemoveSignal(selectedSignals[index])}
                  style={{ marginBottom: 4 }}
                >
                  {name}
                </Tag>
              ))}
            </div>
          </div>
        )}
      </div>

      <Divider style={{ margin: '12px 0', borderColor: theme.surfaceBorder }} />

      {/* 聊天消息区域 */}
      <div 
        ref={chatContainerRef}
        onScroll={handleScroll}
        style={{ 
          flex: 1,
          overflowY: 'auto',
          marginBottom: 16,
          // 关键：在固定高度的父容器内启用内部滚动
          minHeight: 0,
          border: `1px solid ${theme.surfaceBorder}`,
          borderRadius: '8px',
          padding: '12px',
          background: theme.toolbarBackground
        }}
      >
        {messages.length === 0 ? (
          <div style={{ textAlign: 'center', color: theme.textSecondary, padding: '40px 0' }}>
            <RobotOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
            <div>{t('chat.startConversationAfterSelect')}</div>
            <div style={{ fontSize: '12px', marginTop: '4px' }}>
              {t('chat.canAskQuestions')}
            </div>
          </div>
        ) : (
          messages.map((msg, idx) => (
            <div
              key={idx}
              style={{
                marginBottom: 12,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 0
              }}
            >
              <div style={{ flex: 1 }}>
                <div 
                  className={`message-bubble ${msg.role}`}
                  style={{
                    background: msg.role === 'user' ? 'rgba(20,184,166,0.08)' : theme.surfaceBackground,
                    padding: '12px',
                    borderRadius: '8px',
                    border: msg.role === 'user' ? `1px solid ${theme.accentGradientFrom}` : `1px solid ${theme.surfaceBorder}`,
                    boxShadow: msg.role === 'user' ? '0 2px 4px rgba(20,184,166,0.2)' : '0 1px 2px rgba(0, 0, 0, 0.15)',
                    // 为assistant消息添加最大高度和滚动
                    ...(msg.role === 'ai' && {
                      maxHeight: '300px',
                      overflowY: 'auto'
                    })
                  }}>
                  <ReactMarkdown>{msg.content}</ReactMarkdown>
                </div>
                <div style={{
                  fontSize: '12px',
                  color: theme.textSecondary,
                  marginTop: '4px',
                  textAlign: msg.role === 'user' ? 'right' : 'left'
                }}>
                  {msg.timestamp.toLocaleTimeString('zh-CN', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </div>
              </div>
            </div>
          ))
        )}

        {/* 流式消息显示 */}
        {streamingMessage && (
          <div style={{
            marginBottom: 12,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 0
          }}>
            <div style={{ flex: 1 }}>
              <div 
                className="message-bubble ai"
                style={{
                  background: theme.surfaceBackground,
                  padding: '12px',
                  borderRadius: '8px',
                  border: `1px solid ${theme.surfaceBorder}`,
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.15)',
                  // 为流式消息添加最大高度和滚动
                  maxHeight: '300px',
                  overflowY: 'auto'
                }}>
                <ReactMarkdown>{streamingMessage}</ReactMarkdown>
                <span style={{ animation: 'blink 1s infinite' }}>▋</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>


      {/* 输入区域 */}
      <div style={{ display: 'flex', gap: 8 }}>
        <TextArea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSendMessage();
            }
          }}
          placeholder={t('chat.enterYourQuestion')}
          autoSize={{ minRows: 1, maxRows: 4 }}
          style={{ flex: 1, minHeight: 32 }}
          disabled={loading || !canUse}
        />
        <Button
          type="primary"
          icon={<SendOutlined />}
          onClick={handleSendMessage}
          loading={loading}
          disabled={!input.trim() || selectedSignals.length === 0 || !canUse}
          style={{ alignSelf: 'flex-end' }}
        >
          {t('chat.send')}
        </Button>
      </div>

      {/* 提示信息 */}
      {selectedSignals.length === 0 && (
        <div style={{ 
          marginTop: 8, 
          padding: '8px 12px', 
          background: 'rgba(250, 173, 20, 0.08)', 
          border: `1px solid ${theme.surfaceBorder}`,
          borderRadius: '4px',
          fontSize: '12px',
          color: '#fadb14'
        }}>
          💡 {t('chat.pleaseSelectSignalFirst')}
        </div>
      )}
    </div>
  );
};

export default InteractiveChat;
